/**
 * Tiny markdown renderer — supports the subset our help articles use:
 *
 *   # / ## / ###      headings
 *   **bold**          inline strong
 *   *italic*          inline emphasis
 *   `code`            inline code
 *   ```fenced```      code block
 *   - / 1.            unordered + ordered lists
 *   [text](url)       links (rendered with target="_blank" rel="noopener")
 *   > quote           blockquote
 *   ---               horizontal rule
 *   | a | b |         simple GFM-style tables
 *   blank line        paragraph break
 *
 * Sanitises by never accepting raw HTML — escapes `<` and `>` from the source.
 * No external dep. ~200 LOC. Fast enough for ~5 KB articles.
 */

import React from 'react';

interface Props {
  source: string;
  className?: string;
}

const ESCAPE_MAP: Record<string, string> = {
  '<': '&lt;',
  '>': '&gt;',
  '&': '&amp;',
};

function escape(s: string): string {
  return s.replace(/[<>&]/g, (c) => ESCAPE_MAP[c]);
}

/** Render inline tokens (`code`, **bold**, *italic*, [link]). Returns React nodes. */
function renderInline(src: string, key = ''): React.ReactNode {
  // Tokenise greedily by inline patterns, escaping the rest.
  const out: React.ReactNode[] = [];
  let i = 0;
  let buf = '';
  let n = 0;

  function flushBuf() {
    if (buf) {
      out.push(<React.Fragment key={`${key}-t-${n++}`}>{buf}</React.Fragment>);
      buf = '';
    }
  }

  while (i < src.length) {
    const ch = src[i];

    // `code`
    if (ch === '`') {
      const end = src.indexOf('`', i + 1);
      if (end !== -1) {
        flushBuf();
        out.push(
          <code
            key={`${key}-c-${n++}`}
            className="px-1 py-0.5 rounded bg-slate-800/80 text-cyan-200 text-[0.92em] font-mono"
          >
            {src.slice(i + 1, end)}
          </code>,
        );
        i = end + 1;
        continue;
      }
    }

    // **bold**
    if (ch === '*' && src[i + 1] === '*') {
      const end = src.indexOf('**', i + 2);
      if (end !== -1) {
        flushBuf();
        out.push(
          <strong key={`${key}-b-${n++}`} className="font-semibold text-white">
            {renderInline(src.slice(i + 2, end), `${key}-b${n}`)}
          </strong>,
        );
        i = end + 2;
        continue;
      }
    }

    // *italic*
    if (ch === '*') {
      const end = src.indexOf('*', i + 1);
      if (end !== -1 && end > i + 1) {
        flushBuf();
        out.push(
          <em key={`${key}-i-${n++}`} className="italic text-slate-300">
            {renderInline(src.slice(i + 1, end), `${key}-i${n}`)}
          </em>,
        );
        i = end + 1;
        continue;
      }
    }

    // [text](url)
    if (ch === '[') {
      const close = src.indexOf(']', i + 1);
      if (close !== -1 && src[close + 1] === '(') {
        const urlEnd = src.indexOf(')', close + 2);
        if (urlEnd !== -1) {
          flushBuf();
          const text = src.slice(i + 1, close);
          const url = src.slice(close + 2, urlEnd);
          out.push(
            <a
              key={`${key}-l-${n++}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2"
            >
              {text}
            </a>,
          );
          i = urlEnd + 1;
          continue;
        }
      }
    }

    buf += ch;
    i++;
  }
  flushBuf();
  return out;
}

interface Block {
  kind: 'h1' | 'h2' | 'h3' | 'p' | 'ul' | 'ol' | 'pre' | 'quote' | 'hr' | 'table';
  content?: string;
  items?: string[];
  rows?: string[][];
  align?: ('left' | 'center' | 'right')[];
}

/** Parse the markdown source into a flat list of block nodes. */
function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code
    if (line.startsWith('```')) {
      i++;
      const buf: string[] = [];
      while (i < lines.length && !lines[i].startsWith('```')) {
        buf.push(lines[i]);
        i++;
      }
      i++; // skip closing fence
      blocks.push({ kind: 'pre', content: buf.join('\n') });
      continue;
    }

    // Horizontal rule
    if (/^\s*---+\s*$/.test(line)) {
      blocks.push({ kind: 'hr' });
      i++;
      continue;
    }

    // Headings
    if (line.startsWith('### ')) { blocks.push({ kind: 'h3', content: line.slice(4) }); i++; continue; }
    if (line.startsWith('## '))  { blocks.push({ kind: 'h2', content: line.slice(3) }); i++; continue; }
    if (line.startsWith('# '))   { blocks.push({ kind: 'h1', content: line.slice(2) }); i++; continue; }

    // Blockquote
    if (line.startsWith('> ')) {
      const buf: string[] = [];
      while (i < lines.length && lines[i].startsWith('> ')) {
        buf.push(lines[i].slice(2));
        i++;
      }
      blocks.push({ kind: 'quote', content: buf.join(' ') });
      continue;
    }

    // Tables (very minimal GFM)
    if (line.includes('|') && i + 1 < lines.length && /^\s*\|?\s*[-:|\s]+\|\s*[-:|\s]+/.test(lines[i + 1])) {
      const headerCells = line.split('|').map((c) => c.trim()).filter(Boolean);
      const aligns = lines[i + 1].split('|').map((c) => c.trim()).filter(Boolean).map((c) => {
        if (c.startsWith(':') && c.endsWith(':')) return 'center' as const;
        if (c.endsWith(':')) return 'right' as const;
        return 'left' as const;
      });
      i += 2;
      const rows: string[][] = [headerCells];
      while (i < lines.length && lines[i].trim().includes('|')) {
        const cells = lines[i].split('|').map((c) => c.trim());
        if (cells[0] === '') cells.shift();
        if (cells[cells.length - 1] === '') cells.pop();
        rows.push(cells);
        i++;
      }
      blocks.push({ kind: 'table', rows, align: aligns });
      continue;
    }

    // Unordered list
    if (/^\s*-\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*-\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*-\s+/, ''));
        i++;
      }
      blocks.push({ kind: 'ul', items });
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      blocks.push({ kind: 'ol', items });
      continue;
    }

    // Blank line
    if (line.trim() === '') { i++; continue; }

    // Paragraph (consume contiguous non-special lines)
    const buf: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].startsWith('#') &&
      !lines[i].startsWith('- ') &&
      !lines[i].startsWith('> ') &&
      !lines[i].startsWith('```') &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !/^\s*---+\s*$/.test(lines[i])
    ) {
      buf.push(lines[i]);
      i++;
    }
    blocks.push({ kind: 'p', content: buf.join(' ') });
  }

  return blocks;
}

export default function MarkdownLite({ source, className }: Props) {
  // Escape any raw HTML before parsing so user-authored content can't inject.
  const safe = escape(source);
  const blocks = parseBlocks(safe);

  return (
    <div className={`text-slate-200 leading-relaxed ${className ?? ''}`}>
      {blocks.map((b, idx) => {
        switch (b.kind) {
          case 'h1':
            return <h1 key={idx} className="text-2xl font-bold text-white mt-6 mb-3">{renderInline(b.content!, `h1-${idx}`)}</h1>;
          case 'h2':
            return <h2 key={idx} className="text-lg font-bold text-white mt-5 mb-2">{renderInline(b.content!, `h2-${idx}`)}</h2>;
          case 'h3':
            return <h3 key={idx} className="text-sm font-semibold uppercase tracking-wider text-cyan-300 mt-4 mb-1">{renderInline(b.content!, `h3-${idx}`)}</h3>;
          case 'hr':
            return <hr key={idx} className="my-5 border-slate-800" />;
          case 'quote':
            return (
              <blockquote key={idx} className="my-3 pl-3 border-l-2 border-cyan-700 text-slate-300 italic">
                {renderInline(b.content!, `q-${idx}`)}
              </blockquote>
            );
          case 'pre':
            return (
              <pre key={idx} className="my-3 p-3 rounded-lg bg-slate-950/70 border border-slate-800 overflow-x-auto">
                <code className="font-mono text-xs text-cyan-200 whitespace-pre">{b.content}</code>
              </pre>
            );
          case 'ul':
            return (
              <ul key={idx} className="list-disc list-outside pl-5 my-2 space-y-1 marker:text-cyan-500">
                {b.items!.map((it, i) => <li key={i}>{renderInline(it, `ul-${idx}-${i}`)}</li>)}
              </ul>
            );
          case 'ol':
            return (
              <ol key={idx} className="list-decimal list-outside pl-5 my-2 space-y-1 marker:text-cyan-400">
                {b.items!.map((it, i) => <li key={i}>{renderInline(it, `ol-${idx}-${i}`)}</li>)}
              </ol>
            );
          case 'table':
            return (
              <div key={idx} className="my-4 overflow-x-auto rounded-lg border border-slate-800">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-900/80">
                    <tr>
                      {b.rows![0].map((c, ci) => (
                        <th key={ci} className={`px-3 py-2 text-left font-semibold text-cyan-300 ${b.align?.[ci] === 'center' ? 'text-center' : b.align?.[ci] === 'right' ? 'text-right' : ''}`}>
                          {renderInline(c, `th-${idx}-${ci}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows!.slice(1).map((row, ri) => (
                      <tr key={ri} className="border-t border-slate-800">
                        {row.map((c, ci) => (
                          <td key={ci} className={`px-3 py-2 text-slate-200 ${b.align?.[ci] === 'center' ? 'text-center' : b.align?.[ci] === 'right' ? 'text-right' : ''}`}>
                            {renderInline(c, `td-${idx}-${ri}-${ci}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case 'p':
          default:
            return <p key={idx} className="my-2">{renderInline(b.content!, `p-${idx}`)}</p>;
        }
      })}
    </div>
  );
}
